import { z } from "zod";
import { accessFields, actorSchema, idSchema, instantSchema, sha256Schema, sourceDateSchema } from "@amber/contracts";
import { countSchema, httpUrlSchema, unknownValueSchema } from "./shared.js";

export const AVAILABILITY_STATES = [
  "available",
  "edited_by_publisher",
  "replaced_by_publisher",
  "deleted_by_publisher",
  "unavailable",
] as const;
export const availabilityStateSchema = z.enum(AVAILABILITY_STATES);

export const sourceVersionRecordSchema = z.strictObject({
  id: idSchema("ver"),
  item_id: idSchema("itm"),
  version: z.number().int().positive(),
  captured_at: instantSchema,
  origin_url: httpUrlSchema,
  proxy: z.strictObject({ route_id: idSchema("rte"), proxy_label: z.string().min(1) }).nullable(),
  publisher_metadata: z.strictObject({
    title: z.string().min(1).nullable(),
    published: z.union([sourceDateSchema, unknownValueSchema]),
    duration_ms: countSchema.nullable(),
    description_artifact_id: idSchema("art").nullable(),
  }),
  event_date: z.union([sourceDateSchema, unknownValueSchema]),
  change_from_previous: z.array(z.string().min(1)).min(1).optional(),
  availability: z.array(z.strictObject({ observed_at: instantSchema, state: availabilityStateSchema })).min(1),
  last_checked_at: instantSchema,
  manifest_id: idSchema("man"),
  rights_record_id: idSchema("rgt"),
  superseded_by: idSchema("ver").nullable(),
  ...accessFields,
});
export type SourceVersionRecord = z.infer<typeof sourceVersionRecordSchema>;

export const ARTIFACT_KINDS = [
  "original_media",
  "image",
  "page",
  "captions",
  "metadata",
  "robots",
  "derivative_audio",
  "supplier_response",
  "screenshot",
] as const;
export const artifactKindSchema = z.enum(ARTIFACT_KINDS);

export const mediaCharacteristicsSchema = z.strictObject({
  duration_ms: countSchema.nullable(),
  container: z.string().min(1),
  video: z
    .strictObject({ codec: z.string().min(1), width: z.number().int().positive(), height: z.number().int().positive() })
    .optional(),
  audio: z
    .strictObject({
      codec: z.string().min(1),
      sample_rate: z.number().int().positive(),
      channels: z.number().int().positive(),
      bitrate_kbps: z.number().int().positive().optional(),
    })
    .optional(),
});

export const artifactTransformSchema = z.strictObject({
  tool: z.string().min(1),
  version: z.string().min(1),
  steps: z.array(z.string().min(1)).min(1),
  lossless: z.boolean(),
});

export const artifactRecordSchema = z
  .strictObject({
    id: idSchema("art"),
    source_version_id: idSchema("ver"),
    kind: artifactKindSchema,
    sha256: sha256Schema,
    size_bytes: countSchema,
    mime_type: z.string().regex(/^[a-z]+\/[a-z0-9.+-]+$/),
    media: mediaCharacteristicsSchema.nullable(),
    storage_key: z.string().min(1),
    stored_at: instantSchema,
    erased_at: instantSchema.nullable(),
    ...accessFields,
    derived_from: idSchema("art").optional(),
    transform: artifactTransformSchema.optional(),
    note: z.string().min(1).optional(),
  })
  .refine((a) => (a.derived_from === undefined) === (a.transform === undefined), {
    message: "a derivative names both its source artifact and its transform",
    path: ["transform"],
  })
  .refine((a) => a.kind !== "derivative_audio" || a.derived_from !== undefined, {
    message: "derivative audio names the artifact it was derived from",
    path: ["derived_from"],
  });
export type ArtifactRecord = z.infer<typeof artifactRecordSchema>;

export const captureManifestRecordSchema = z.strictObject({
  id: idSchema("man"),
  source_version_id: idSchema("ver"),
  artifact_ids: z.array(idSchema("art")).min(1),
  integrity: z.strictObject({
    size_matches: z.boolean(),
    duration_matches: z.boolean(),
    decoded: z.boolean(),
    truncated: z.boolean(),
    checked_at: instantSchema,
  }),
  storage_key: z.string().min(1),
  sha256: sha256Schema,
  written_at: instantSchema,
  derivative_ids: z.array(idSchema("art")).optional(),
  supersedes: idSchema("man").nullable().optional(),
});
export type CaptureManifestRecord = z.infer<typeof captureManifestRecordSchema>;

export const RIGHTS_FIELDS = ["declared_terms", "holder", "permission", "restrictions", "reuse_terms"] as const;
export const rightsFieldSchema = z.enum(RIGHTS_FIELDS);

export const RIGHTS_BASES = ["publisher_metadata", "page_byline", "terms_page", "official_record", "editor"] as const;
export const rightsBasisSchema = z.enum(RIGHTS_BASES);

export const rightsRecordSchema = z.strictObject({
  id: idSchema("rgt"),
  source_version_id: idSchema("ver"),
  declared_terms: z.union([
    z.strictObject({
      value: z.string().min(1),
      evidence_url: httpUrlSchema,
      snapshot_artifact_id: idSchema("art").nullable(),
    }),
    unknownValueSchema,
  ]),
  holder: z.union([z.strictObject({ value: z.string().min(1), basis: rightsBasisSchema }), unknownValueSchema]),
  permission: z.discriminatedUnion("state", [
    z.strictObject({ state: z.literal("not_obtained") }),
    z.strictObject({
      state: z.literal("obtained"),
      granted_by: z.string().min(1),
      obtained_at: instantSchema,
      evidence_artifact_id: idSchema("art").nullable(),
    }),
  ]),
  restrictions: z.array(z.string().min(1)),
  unknowns: z.array(rightsFieldSchema),
  observed_at: instantSchema,
  assessments: z.array(
    z.strictObject({
      at: instantSchema,
      by: actorSchema,
      conclusion: z.string().min(1),
      evidence_url: httpUrlSchema.nullable(),
    }),
  ),
  snapshot_artifact_id: idSchema("art").nullable(),
});
export type RightsRecord = z.infer<typeof rightsRecordSchema>;

export const CAPTURE_MANIFEST_V1 = "amber.capture-manifest/1";
export const CAPTURE_MANIFEST_V2 = "amber.capture-manifest/2";

const manifestFileSchema = z.object({
  id: z.string().min(1),
  kind: z.string().min(1),
  sha256: sha256Schema,
  size_bytes: countSchema,
  mime_type: z.string().min(1),
  storage_key: z.string().min(1),
  media: z.unknown().optional(),
});

export const manifestIntegritySchema = z
  .object({ size_matches: z.boolean(), duration_matches: z.boolean(), decoded: z.boolean(), truncated: z.boolean() })
  .loose();

const manifestBaseShape = {
  id: z.string().min(1),
  item_id: z.string().min(1),
  source_version_id: z.string().min(1),
  origin_url: z.string().min(1),
  route_id: z.string().nullable(),
  proxy_label: z.string().nullable().optional(),
  attempt_id: z.string().nullable(),
  captured_at: instantSchema,
  artifacts: z.array(manifestFileSchema),
  integrity: manifestIntegritySchema,
};

export const manifestRightsSchema = z.object({
  id: z.string().min(1),
  declared_terms: rightsRecordSchema.shape.declared_terms,
  holder: rightsRecordSchema.shape.holder,
  permission: rightsRecordSchema.shape.permission,
  restrictions: z.array(z.string().min(1)),
  unknowns: z.array(rightsFieldSchema),
  observed_terms: z.array(z.object({ kind: z.string(), url: z.string(), verdict: z.string() }).loose()),
  observed_at: instantSchema,
  assessments: rightsRecordSchema.shape.assessments,
  snapshot_artifact_id: z.string().nullable(),
});

export const manifestDerivativeSchema = z.object({
  id: z.string().min(1),
  kind: z.string().min(1),
  derived_from: z.string().min(1),
  sha256: sha256Schema,
  size_bytes: countSchema,
  mime_type: z.string().min(1),
  storage_key: z.string().min(1),
  transform: z.record(z.string(), z.unknown()),
});

// QO-32: manifests are write-once, so every version ever written must keep parsing; version 1 has no rights record
// or derivative list.
export const captureManifestDocumentSchema = z.discriminatedUnion("schema", [
  z.object({ schema: z.literal(CAPTURE_MANIFEST_V1), ...manifestBaseShape }),
  z.object({
    schema: z.literal(CAPTURE_MANIFEST_V2),
    ...manifestBaseShape,
    written_at: instantSchema,
    supersedes: z.object({ id: z.string().min(1), storage_key: z.string().min(1) }).nullable(),
    rights: manifestRightsSchema.nullable(),
    derivatives: z.array(manifestDerivativeSchema),
  }),
]);
export type CaptureManifestDocument = z.infer<typeof captureManifestDocumentSchema>;
